import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfig } from '@client/providers/ConfigProvider';
import { useScheduledAppVersionCheck } from '@client/hooks/use-app-version-check-schedule.hooks';
import amplitudeLog from '@client/amplitude';
import { AMP_APP_PAGE_REFRESH_DUE_TO_NEW_APP_VERSION } from '@shared/constants';
import {
  forceReloadApp,
  clearVersionMismatchRedirectFlag,
} from '@client/store/store.helpers';
import {
  applyAppVersionCheck,
  VERSION_MISMATCH_PATH,
} from '@client/helpers/app-version-reload.helpers';

type VersionCheckLiveState = {
  version: string;
  pathname: string;
  search: string;
  navigate: ReturnType<typeof useNavigate>;
};

/**
 * Keeps client and server package versions aligned via GET /version (localhost too).
 *
 * Checks on mount, when the client version changes, when entering or leaving
 * `/new-version`, when the tab is focused or shown again, and once an hour
 * while it stays visible. A check reloads only when the server version differs.
 * An in-flight check from before HMR/rebase must not send the user to
 * `/new-version` after versions already match.
 *
 * On `/new-version`, polls until versions match (typical after a local rebase)
 * and then returns to the stored in-app URL.
 */
export const VersionChecker: React.FC = () => {
  const [{ apiUrl, version }] = useConfig();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const liveRef = useRef<VersionCheckLiveState>({
    version,
    pathname,
    search,
    navigate,
  });
  liveRef.current = { version, pathname, search, navigate };

  const checkVersion = useCallback(() => {
    const live = liveRef.current;
    void applyAppVersionCheck({
      apiUrl,
      localAppVersion: live.version,
      pathname: live.pathname,
      search: live.search,
      getCurrentSnapshot: () => ({
        pathname: liveRef.current.pathname,
        search: liveRef.current.search,
        localAppVersion: liveRef.current.version,
      }),
      handlers: {
        onSynced: clearVersionMismatchRedirectFlag,
        onRecover: (returnTo) => {
          clearVersionMismatchRedirectFlag();
          live.navigate(returnTo, { replace: true });
        },
        onReload: (serverVersion) => {
          // A matching version must not navigate, including the hourly check.
          if (serverVersion === live.version) return;
          amplitudeLog(AMP_APP_PAGE_REFRESH_DUE_TO_NEW_APP_VERSION, {
            currentVersion: serverVersion,
            reloadVersion: live.version,
          });
          forceReloadApp(live.version);
        },
      },
    });
  }, [apiUrl]);

  const isVersionMismatchPage = pathname === VERSION_MISMATCH_PATH;

  // Mount, client version HMR, and entering/leaving /new-version — not every route.
  useEffect(() => {
    checkVersion();
  }, [checkVersion, version, isVersionMismatchPage]);

  useScheduledAppVersionCheck(checkVersion, { isVersionMismatchPage });

  return null;
};
